import React, { useContext, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  LinearProgress,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router';
import {
  ACTIVE_JOB_STATUSES,
  JobTableType,
  StatusValue,
} from 'api/jobApi';
import { buildJobListUrl } from '../jobListUrlParams';
import useIsMobile from 'theme/useIsMobile';
import { OverviewAPI } from 'api';
import { useQuery } from '@tanstack/react-query';
import { JobsByCoordinator } from 'api/overviewApi';
import UserContext from 'state/UserContext';
import StatusFilterSelect, {
  allStatus,
  isActiveSelection,
} from './StatusFilter';
import DateRangeFilter from './DateRangeFilter';
import {
  cardSx,
  chartTokens,
  columnHeaderSx,
  footerSx,
  formatCount,
  getInitials,
  subtitleSx,
  tabularNumSx,
  titleSx,
} from './dashboardChartTokens';

/** Coordinators listed before the "See all coordinators" link is used. */
const COLLAPSED_ROW_COUNT = 10;

interface WorkloadRow {
  userId: string;
  name: string;
  initials: string;
  jobs: number;
  isCurrentUser: boolean;
}

const WorkloadBar = ({
  row,
  scaleMax,
}: {
  row: WorkloadRow;
  scaleMax: number;
}) => {
  const fillPercent = scaleMax > 0 ? (row.jobs / scaleMax) * 100 : 0;

  return (
    <Box
      sx={{
        position: 'relative',
        height: 26,
        borderRadius: '6px',
        backgroundColor: chartTokens.track,
        overflow: 'hidden',
      }}>
      <Box
        sx={{
          height: '100%',
          width: `${fillPercent}%`,
          minWidth: row.jobs > 0 ? 6 : 0,
          borderRadius: '6px',
          backgroundColor: chartTokens.accent,
          transition: 'width 300ms ease',
        }}
      />
    </Box>
  );
};

const JobsByCoordinatorChart = () => {
  const navigate = useNavigate();
  // Only drop the bar column on phones — it is the point of the card.
  const isMobile = useIsMobile('xs');
  const { user } = useContext(UserContext);

  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const [endDate, setEndDate] = useState<string | undefined>(undefined);
  const [rangeSummary, setRangeSummary] = useState('all time');

  const [showAll, setShowAll] = useState(false);

  const { isFetching, data } = useQuery(
    ['coordinator-job-count', startDate, endDate],
    () => OverviewAPI.fetchJobCountByCoordinator(startDate, endDate)
  );

  // The statuses that make up an open workload are selected by default.
  const [selectedStatus, setSelectedStatus] =
    useState<StatusValue[]>(ACTIVE_JOB_STATUSES);

  /**
   * Drill-through to one coordinator's jobs in exactly the statuses the bar
   * counts. The statuses are passed explicitly (rather than landing on the
   * open tab) because the open tab's default also includes Not started,
   * which the chart excludes — the list should match the number clicked.
   * The date range has no URL equivalent and is left unfiltered.
   */
  const drillToCoordinatorJobs = (userId: string) =>
    navigate(
      buildJobListUrl({
        tab: JobTableType.ALL,
        status: selectedStatus.length ? selectedStatus : undefined,
        coordinator: [userId],
      })
    );

  const rows: WorkloadRow[] = useMemo(() => {
    const statuses = selectedStatus.length
      ? selectedStatus
      : allStatus.map((ob) => ob.label);

    return ((data ?? []) as JobsByCoordinator[])
      .map((coordinator) => ({
        userId: `${coordinator.userId}`,
        name: `${coordinator.firstName} ${coordinator.lastName}`.trim(),
        initials: getInitials(coordinator.firstName, coordinator.lastName),
        jobs: statuses.reduce(
          (total, status) => total + (Number(coordinator[status]) || 0),
          0
        ),
        isCurrentUser: `${coordinator.userId}` === `${user?.id}`,
      }))
      .sort((a, b) => b.jobs - a.jobs);
  }, [data, selectedStatus, user?.id]);

  const visibleRows = showAll ? rows : rows.slice(0, COLLAPSED_ROW_COUNT);
  const totalJobs = visibleRows.reduce((total, row) => total + row.jobs, 0);
  // Bars are relative to the busiest coordinator: there is no configurable
  // capacity to measure them against yet.
  const scaleMax = Math.max(...rows.map((row) => row.jobs), 1);

  // Naming every status makes for an unreadable subtitle once a few are on.
  const statusSummary = !selectedStatus.length
    ? 'All jobs'
    : isActiveSelection(selectedStatus)
    ? 'Open jobs'
    : selectedStatus.length <= 2
    ? `${selectedStatus.join(' and ')} jobs`
    : `Jobs across ${selectedStatus.length} statuses`;

  return (
    <Card sx={cardSx}>
      {Boolean(isFetching) && <LinearProgress />}
      <CardContent sx={{ padding: { xs: 2.5, md: 3 }, paddingBottom: 0 }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.5,
            alignItems: 'flex-start',
            justifyContent: 'space-between',
          }}>
          <Box>
            <Typography sx={titleSx}>Coordinator Workload</Typography>
            <Typography sx={subtitleSx}>
              {`${statusSummary}, highest first · ${formatCount(
                totalJobs
              )} jobs shown · ${rangeSummary}`}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              alignItems: 'center',
            }}>
            <StatusFilterSelect
              value={selectedStatus}
              onChange={setSelectedStatus}
              width={520}
            />

            <DateRangeFilter
              onChange={(start, end, summary) => {
                setStartDate(start);
                setEndDate(end);
                setRangeSummary(summary);
              }}
            />
          </Box>
        </Box>
      </CardContent>

      <Divider sx={{ borderColor: chartTokens.divider, marginTop: 2 }} />

      <CardContent
        sx={{
          flex: 1,
          padding: { xs: 2.5, md: 3 },
          paddingTop: { xs: 2, md: 2 },
        }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: isMobile
              ? 'minmax(0, 1fr) 48px'
              : 'minmax(0, 220px) minmax(0, 1fr) 56px',
            alignItems: 'center',
            columnGap: 2,
            paddingBottom: 1.5,
          }}>
          <Typography sx={columnHeaderSx}>Project Coordinator</Typography>
          {!isMobile && <Typography sx={columnHeaderSx}>Open Jobs</Typography>}
          <Typography sx={{ ...columnHeaderSx, textAlign: 'right' }}>
            Jobs
          </Typography>
        </Box>
        <Divider sx={{ borderColor: chartTokens.divider }} />

        {isFetching && !rows.length
          ? Array.from({ length: 6 }).map((_, index) => (
              <Skeleton key={index} height={52} />
            ))
          : visibleRows.map((row) => (
              <Box
                key={row.userId}
                role="button"
                tabIndex={0}
                onClick={() => drillToCoordinatorJobs(row.userId)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    drillToCoordinatorJobs(row.userId);
                  }
                }}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: isMobile
                    ? 'minmax(0, 1fr) 48px'
                    : 'minmax(0, 220px) minmax(0, 1fr) 56px',
                  alignItems: 'center',
                  columnGap: 2,
                  paddingY: 1.25,
                  borderBottom: `1px solid ${chartTokens.divider}`,
                  cursor: 'pointer',
                  transition: 'background-color 150ms ease',
                  '&:hover': { backgroundColor: chartTokens.accentMuted },
                }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    minWidth: 0,
                  }}>
                  <Box
                    sx={{
                      flexShrink: 0,
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      fontWeight: 700,
                      backgroundColor: row.isCurrentUser
                        ? chartTokens.accent
                        : chartTokens.accentSoft,
                      color: row.isCurrentUser ? '#FFFFFF' : chartTokens.accent,
                    }}>
                    {row.initials}
                  </Box>
                  <Tooltip title={row.name}>
                    <Typography
                      noWrap
                      sx={{
                        fontSize: 14.5,
                        fontWeight: row.isCurrentUser ? 700 : 500,
                        color: chartTokens.heading,
                      }}>
                      {row.name}
                    </Typography>
                  </Tooltip>
                  {row.isCurrentUser && (
                    <Typography
                      component="span"
                      sx={{
                        flexShrink: 0,
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: '0.06em',
                        color: chartTokens.accent,
                      }}>
                      YOU
                    </Typography>
                  )}
                </Box>

                {!isMobile && <WorkloadBar row={row} scaleMax={scaleMax} />}

                <Typography
                  sx={{
                    fontSize: 15,
                    fontWeight: 700,
                    textAlign: 'right',
                    color: chartTokens.heading,
                    ...tabularNumSx,
                  }}>
                  {formatCount(row.jobs)}
                </Typography>
              </Box>
            ))}

        {!isFetching && !rows.length && (
          <Typography sx={{ ...footerSx, paddingY: 3, textAlign: 'center' }}>
            No coordinators to show for this selection.
          </Typography>
        )}

        {Boolean(rows.length) && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingTop: 2,
            }}>
            <Typography sx={footerSx}>
              {`Showing ${visibleRows.length} of ${rows.length} coordinator${
                rows.length === 1 ? '' : 's'
              } · bars are relative to the busiest coordinator`}
            </Typography>
            {rows.length > COLLAPSED_ROW_COUNT && (
              <Button
                disableRipple
                endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                onClick={() => setShowAll((current) => !current)}
                sx={{
                  textTransform: 'none',
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: chartTokens.accent,
                  padding: 0,
                  minWidth: 0,
                  '&:hover': { backgroundColor: 'transparent' },
                }}>
                {showAll ? 'Show top 10' : 'See all coordinators'}
              </Button>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default JobsByCoordinatorChart;
